/**
 * InsightsStep — create-wizard step 3 «Insights» (W13 IS2, AC-W13-06).
 *
 * The THESES of the pinned analysis, every one of them checked when the step
 * opens: the shipped behaviour is «all theses ground the run», and this step
 * only lets the operator narrow it. Unchecking is therefore the only edit that
 * changes the request — which is why the summary states the rule, not just the
 * count.
 *
 * Props-in / JSX-out, on the one `SelectionTable` recipe (designer invariant
 * 24). The rules live in `StrategyFormDialog`: the defaults, the scopes the
 * request narrows (ruling W13-R22), the per-scope refusal and the budget
 * warnings are computed there from the SAME feasibility contract the server
 * uses, and handed here as sentences.
 *
 * Mock: `.design/create-strategy-wizard/mockups/insights-step.html`.
 */
import React, { useMemo } from 'react';
import { Alert, AlertTitle, Box, Button, Chip, Stack, Typography } from '@mui/material';
import {
  GLOBAL_INSIGHT_SELECTION_BUDGET,
  TASK_INSIGHT_SELECTION_BUDGET,
} from '@modules/shared-types';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import type { SDataTableColumnDef } from '@modules/ui-components/dialogs/SDataTable';
import { SelectionTable } from '../SelectionTable';
import type { InsightThesisSelectionRow } from '../useWizardSourceRows';

/** One scope the selection left with nothing — and the sentence that says so. */
export interface InsightStarvedScope {
  /** `global` or the task id — what makes the row addressable (AC-W13-04). */
  scopeId: string;
  /** The feasibility contract's own wording, identical to the server's 400. */
  message: string;
  /** «Select all theses of "Build muscle"» — the contained fix's label (MF-5). */
  fixLabel: string;
}

export interface InsightsStepProps {
  rows: InsightThesisSelectionRow[];
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  /** No analysis pinned yet — step 1 is the only required decision. */
  pinned: boolean;
  loading?: boolean;
  error?: string | null;
  /** Displayed scopes the selection starved. Empty = the request is sendable. */
  starvedScopes: InsightStarvedScope[];
  /**
   * REVIEW tier 2, MF-5 (invariant 26): re-check every thesis of one scope —
   * `'global'` or a task id. The refusal is never a dead end. Omitted = no
   * inline fix is offered (the caller has none to give).
   */
  onSelectScopeTheses?: (scopeId: string) => void;
  /** Per-scope «above the N recommended» sentences (AC-W13-25). Never blocking. */
  budgetWarnings: string[];
  disabled?: boolean;
}

/**
 * The server's DEFAULT shortlist per scope — what travels while nothing is
 * unchecked (ruling W13-R27). Derived from the budgets rather than re-declared:
 * `strategyFeasibility` documents the budget as exactly 2 × the shortlist
 * (ruling W13-R2), and builder-service's own `GLOBAL_INSIGHTS_TOP_N` /
 * `PER_TASK_INSIGHTS_TOP_N` are not importable from the frontend bundle.
 */
export const DEFAULT_GLOBAL_TOP_N = GLOBAL_INSIGHT_SELECTION_BUDGET / 2;
export const DEFAULT_TASK_TOP_N = TASK_INSIGHT_SELECTION_BUDGET / 2;

/** «9.5» / «12» — the ranking the default projection uses, not a raw float tail. */
const formatWeight = (weight: number): string =>
  (Number.isInteger(weight) ? String(weight) : weight.toFixed(1));

/**
 * REVIEW tier 2, MF-1 — the column budget of the 800px column.
 *
 * MRT gives every column 180px by default, which made the THESIS — the sentence
 * the prompt actually carries — as narrow as «Actors»: the table measured 960px
 * inside a 798px container (Weight off-viewport at every breakpoint) and each
 * thesis wrapped into 8–10 lines, 2.6 rows per screen. The four judgement
 * columns are capped and the thesis takes everything they leave (`grow`, which
 * needs `layoutMode: 'grid'`); the text itself is clamped to two lines with the
 * full sentence on `title`, so a row can never grow past ~56px again.
 *
 * Fix round 4: the budget is struck against the REAL select gutter. MRT's
 * `mrt-row-select` display column is 60px wide with select-all (round 2
 * measured the grid track at 830px in a 798px box — the ~30px the first budget
 * assumed clipped «Weight» to «Wei» and the scope chips mid-word). In grid
 * layout every fixed column is `flex: 0 0 <size>` and the thesis grows from
 * its size, so `gutter + Σ sizes` must fit the paper's inner width.
 */
/** The 800px column minus the outlined paper's 1px border on each side. */
export const INSIGHT_TABLE_WIDTH_BUDGET = 798;
/** MRT's `mrt-row-select` display column with a select-all header. */
export const INSIGHT_SELECT_COLUMN_SIZE = 60;
export const INSIGHT_COLUMN_SIZES = {
  /** Grows into the free width; the size is its FLOOR, not its cap. */
  thesis: { size: 330, minSize: 240 },
  category: { size: 110, maxSize: 140 },
  /** 80 prints «Actors» whole — 72 ellipsised its own header. */
  actors: { size: 80, maxSize: 90 },
  scope: { size: 130, maxSize: 160 },
  weight: { size: 80, maxSize: 90 },
} as const;

/** Rows stay one text line high at the widest thesis (MF-1). */
export const INSIGHT_THESIS_MAX_LINES = 2;

export const InsightsStep: React.FC<InsightsStepProps> = ({
  rows,
  selectedIds,
  onSelectedIdsChange,
  pinned,
  loading = false,
  error = null,
  starvedScopes,
  onSelectScopeTheses,
  budgetWarnings,
  disabled = false,
}) => {
  const columns = useMemo<SDataTableColumnDef<InsightThesisSelectionRow>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Thesis',
      ...INSIGHT_COLUMN_SIZES.thesis,
      grow: true,
      Cell: ({ row }) => (
        <Typography
          variant="body2"
          // The sentence the prompt carries, two lines high; the whole of it
          // stays reachable to a pointer and to a screen reader (MF-1).
          title={row.original.name}
          sx={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: INSIGHT_THESIS_MAX_LINES,
            overflow: 'hidden',
          }}
        >
          {row.original.name}
        </Typography>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Category',
      ...INSIGHT_COLUMN_SIZES.category,
      grow: false,
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary" noWrap title={row.original.category}>
          {row.original.category}
        </Typography>
      ),
    },
    {
      accessorKey: 'actorCount',
      header: 'Actors',
      ...INSIGHT_COLUMN_SIZES.actors,
      grow: false,
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary">{row.original.actorCount}</Typography>
      ),
    },
    {
      accessorKey: 'scopeLabel',
      header: 'Scope',
      ...INSIGHT_COLUMN_SIZES.scope,
      grow: false,
      Cell: ({ row }) => {
        // Chips, never a code identifier or a wrapping sentence: the scope is a
        // glance, and a thesis two tasks share stays one row (MF-1).
        const labels = row.original.scope === 'global'
          ? ['Global']
          : row.original.scopeLabel.split(' · ');
        const [first, ...rest] = labels;
        return (
          // Fix round 4: the cell is overflow-safe. The first chip may shrink
          // to the cell (its label ellipsises — round 2 found a 241px chip
          // sliced at 150px with no ellipsis because nothing let it shrink);
          // further scopes fold into «+N», the full list on its `title`.
          <Stack direction="row" spacing={0.5} sx={{ minWidth: 0, maxWidth: '100%' }}>
            <Chip
              label={first}
              size="small"
              variant="outlined"
              title={first}
              sx={{ minWidth: 0, maxWidth: '100%' }}
            />
            {rest.length > 0 && (
              <Chip
                label={`+${rest.length}`}
                size="small"
                variant="outlined"
                title={labels.join(' · ')}
                data-testid={STRATEGY_LIST.wizardInsightsScopeMore(row.original.id)}
                sx={{ flexShrink: 0 }}
              />
            )}
          </Stack>
        );
      },
    },
    {
      accessorKey: 'cumulativeWeight',
      header: 'Weight',
      ...INSIGHT_COLUMN_SIZES.weight,
      grow: false,
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary">
          {formatWeight(row.original.cumulativeWeight)}
        </Typography>
      ),
    },
  ], []);

  /**
   * Ruling W13-R27 — the summary states what is SENT.
   *
   * Nothing unchecked ⇒ the request carries no `insightIds`, so the server
   * renders its own shortlist and the count on this screen is NOT what travels;
   * saying «14 of 14 selected» alone would be a quiet lie. Unchecking anything
   * makes the selection literal — every checked thesis is sent, ranking and all.
   */
  const narrowed = rows.length > 0 && selectedIds.length !== rows.length;
  const summary = (() => {
    if (loading) return 'Loading theses…';
    if (error) return '';
    if (rows.length === 0) return '';
    const counted = `${selectedIds.length} of ${rows.length} selected`;
    return narrowed
      ? `${counted} · exactly these theses are sent, whatever their ranking`
      : `${counted} · while nothing is unchecked, the analysis’ top ${DEFAULT_GLOBAL_TOP_N} global`
        + ` and top ${DEFAULT_TASK_TOP_N} per-task theses by weight are sent`;
  })();

  return (
    // MF-2: a growing flex column, so the table's scroll box fills the height
    // the step has left and the summary/footer under the box stay on screen at
    // 1280×800. No `minHeight: 0`: the automatic minimum is the table's floor,
    // and a step that cannot fit even that spills into the dialog's scroller
    // rather than clipping its own table (fix round 4).
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
      {/* The refusal comes FIRST (designer invariant 26: the blocker sits above
          the thing it invalidates) and names the scope, exactly as the server's
          `task_without_theses` blocker does two lines later on the review step. */}
      {starvedScopes.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid={STRATEGY_LIST.wizardInsightsStarved}>
          <AlertTitle sx={{ mb: 0.5 }}>This selection cannot be generated from</AlertTitle>
          {starvedScopes.map((scope) => (
            <Box
              key={scope.scopeId}
              data-testid={STRATEGY_LIST.wizardInsightsStarvedScope(scope.scopeId)}
              sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}
            >
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{scope.message}</Typography>
              {/* Invariant 26 / FP-6: the refusal carries the one click that
                  ends it, right where it is stated (MF-5). */}
              {onSelectScopeTheses && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={disabled}
                  onClick={() => onSelectScopeTheses(scope.scopeId)}
                  data-testid={STRATEGY_LIST.wizardInsightsStarvedFix(scope.scopeId)}
                  sx={{ flexShrink: 0, textTransform: 'none' }}
                >
                  {scope.fixLabel}
                </Button>
              )}
            </Box>
          ))}
        </Alert>
      )}

      <SelectionTable<InsightThesisSelectionRow>
        rows={rows}
        selectedIds={selectedIds}
        onSelectedIdsChange={onSelectedIdsChange}
        columns={columns}
        loading={loading}
        error={error}
        disabled={disabled}
        searchPlaceholder="Search theses"
        summary={summary}
        // AC-W13-25 + ruling W13-R27: over-wide is TOLD, never truncated and
        // never blocked — and only while the selection actually narrows, since
        // an unnarrowed request grows no prompt at all.
        footer={narrowed && budgetWarnings.length > 0 ? budgetWarnings.join(' ') : undefined}
        emptyState={(
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            {pinned
              ? 'This analysis exposes no theses yet — generation will fall back to its own projection.'
              : 'Pin an insight analysis on the first step: its theses become the grounding offered here.'}
          </Typography>
        )}
        fillHeight
        // MF-1: `grow` on the Thesis column needs MRT's grid layout.
        columnLayout="grid"
        aria-label="Insight theses"
        data-testid={STRATEGY_LIST.wizardTable('insights')}
      />
    </Box>
  );
};

InsightsStep.displayName = 'InsightsStep';
